import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import KYCAlertBanner from '@/components/KYCAlertBanner';
import { 
  Building2, 
  CheckCircle, 
  Clock, 
  Users,
  Calendar,
  MapPin,
  Phone,
  Mail,
  Globe,
  Settings,
  Package,
  ShieldCheck,
  QrCode,
  CalendarDays,
  Coins,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';

const facilityTypeLabels = {
  hospital: 'Hôpital',
  clinic: 'Clinique',
  health_center: 'Centre de santé',
  pharmacy: 'Pharmacie',
  laboratory: 'Laboratoire',
  imaging_center: "Centre d'imagerie",
  maternity: 'Maternité',
  dental_clinic: 'Cabinet dentaire',
  other: 'Autre',
};

const FacilityDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [facilityProfile, setFacilityProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProfessionals: 0,
    totalAppointments: 0,
    activeQueues: 0,
  });

  const fetchData = useCallback(async () => {
    try {
      const profileRes = await api.get('/facility/profile');
      setFacilityProfile(profileRes.data);
      
      // Fetch additional stats if needed
      try {
        const [prosRes, queuesRes] = await Promise.all([
          api.get('/facility-professionals').catch(() => ({ data: { professionals: [] } })),
          api.get('/queue/facility').catch(() => ({ data: { queues: [] } }))
        ]);
        
        setStats({
          totalProfessionals: prosRes.data?.professionals?.length || 0,
          totalAppointments: 0, // TODO: fetch from appointments
          activeQueues: queuesRes.data?.queues?.filter(q => q.active)?.length || 0,
        });
      } catch (e) {
        console.log('Stats loading...');
      }
    } catch (error) {
      console.log('Loading facility data...');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-[#E53935] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 md:px-0" data-testid="facility-dashboard">
      {/* KYC Alert Banner */}
      <KYCAlertBanner userRole="facility" />
      
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-[#0D47A1] to-[#1565C0] rounded-2xl p-4 sm:p-6 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold mb-1 sm:mb-2 leading-tight text-white drop-shadow-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Bienvenue sur votre tableau de bord
            </h1>
            <p className="text-white text-sm sm:text-base font-medium truncate">
              {facilityProfile?.name || user?.facility_legal_name || 'Votre établissement'}
            </p>
            <p className="text-blue-100 flex items-center gap-2 text-xs sm:text-sm mt-1">
              <Building2 className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
              <span className="truncate">{facilityTypeLabels[user?.facility_type] || 'Établissement de santé'}</span>
            </p>
          </div>
          <Badge 
            variant={facilityProfile?.is_verified ? "default" : "secondary"}
            className={`flex-shrink-0 self-start ${
              facilityProfile?.is_verified 
                ? "bg-green-500 hover:bg-green-600 text-white" 
                : "bg-amber-400 hover:bg-amber-500 text-amber-900"
            }`}
          >
            {facilityProfile?.is_verified ? (
              <><CheckCircle className="w-3 h-3 mr-1" /> Vérifié</>
            ) : (
              <><Clock className="w-3 h-3 mr-1" /> En attente</>
            )}
          </Badge>
        </div>
      </div>

      {/* Verification Status Banner - Simple */}
      {!facilityProfile?.is_verified && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <ShieldCheck className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-yellow-800">Vérification en attente</h3>
                  <p className="text-sm text-yellow-700">
                    Soumettez vos documents pour activer toutes les fonctionnalités
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/facility/verification')}
                className="bg-yellow-600 hover:bg-yellow-700 text-white w-full sm:w-auto"
                data-testid="go-to-verification-btn"
              >
                Vérifier mon établissement
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-xl">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.totalProfessionals}</p>
                <p className="text-xs text-slate-500">Professionnels</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-xl">
                <Calendar className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.totalAppointments}</p>
                <p className="text-xs text-slate-500">RDV ce mois</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-xl">
                <QrCode className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.activeQueues}</p>
                <p className="text-xs text-slate-500">Files actives</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-xl">
                <Coins className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">0</p>
                <p className="text-xs text-slate-500">Crédits</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Actions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* File d'attente */}
        <Card className="hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => navigate('/facility/queue')}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-purple-100 rounded-xl group-hover:bg-purple-200 transition-colors">
                <QrCode className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-1">File d&apos;attente</h3>
                <p className="text-sm text-slate-500">
                  Gérez vos files d&apos;attente numériques avec QR code
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 transition-colors" />
            </div>
          </CardContent>
        </Card>

        {/* Catalogue Produits */}
        <Card className="hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => navigate('/facility/products')}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-red-100 rounded-xl group-hover:bg-red-200 transition-colors">
                <Package className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-1">Catalogue</h3>
                <p className="text-sm text-slate-500">
                  Gérez vos produits et services
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 transition-colors" />
            </div>
          </CardContent>
        </Card>

        {/* Professionnels */}
        <Card className="hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => navigate('/facility/professionals')}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-xl group-hover:bg-blue-200 transition-colors">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-1">Équipe</h3>
                <p className="text-sm text-slate-500">
                  Gérez vos professionnels affiliés
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 transition-colors" />
            </div>
          </CardContent>
        </Card>

        {/* Événements */}
        <Card className="hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => navigate('/facility/events')}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-green-100 rounded-xl group-hover:bg-green-200 transition-colors">
                <CalendarDays className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-1">Événements</h3>
                <p className="text-sm text-slate-500">
                  Créez et gérez vos événements
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 transition-colors" />
            </div>
          </CardContent>
        </Card>

        {/* Crédits */}
        <Card className="hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => navigate('/facility/credits')}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-xl group-hover:bg-amber-200 transition-colors">
                <Coins className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-1">Crédits & Abonnement</h3>
                <p className="text-sm text-slate-500">
                  Gérez vos crédits et votre abonnement
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 transition-colors" />
            </div>
          </CardContent>
        </Card>

        {/* Vérification KYC */}
        <Card className="hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => navigate('/facility/verification')}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-xl group-hover:opacity-80 transition-opacity ${
                facilityProfile?.is_verified ? 'bg-green-100' : 'bg-yellow-100'
              }`}>
                <ShieldCheck className={`w-6 h-6 ${
                  facilityProfile?.is_verified ? 'text-green-600' : 'text-yellow-600'
                }`} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 mb-1">Vérification</h3>
                <p className="text-sm text-slate-500">
                  {facilityProfile?.is_verified ? 'Documents approuvés' : 'Documents en attente'}
                </p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 transition-colors" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Facility Profile Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base sm:text-lg">Profil de l&apos;établissement</CardTitle>
              <CardDescription className="text-xs sm:text-sm">Informations publiques visibles dans l&apos;annuaire</CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/facility/profile')}
              className="w-full sm:w-auto"
              data-testid="edit-facility-profile-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Modifier le profil
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {facilityProfile?.address ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{facilityProfile.address}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <span>{facilityProfile.phone || 'Non renseigné'}</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{facilityProfile.email || user?.email}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <Globe className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{facilityProfile.website || 'Non renseigné'}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 mb-4 text-sm">
                Complétez votre profil pour apparaître dans l&apos;annuaire DOCKHTA
              </p>
              <Button 
                onClick={() => navigate('/facility/profile')}
                data-testid="complete-profile-btn"
              >
                Compléter le profil
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FacilityDashboard;
